import { AxiosError } from 'axios'
import * as TypedError from 'error/typed'
import { Request } from 'express'
import get from 'lodash-es/get'
import isError from 'lodash-es/isError'
import isString from 'lodash-es/isString'

import { collect } from './eventsApi'
import rollbar from './rollbar'

const AXIOS_RESPONSE_DATA_MAX_LENGTH = 1000

export const UndefinedError = TypedError({
  type: 'undefined',
  message: 'Exception or rejection value was null or undefined'
})

export const NotAnError = TypedError({
  type: 'not-an-error',
  message: 'Exception or rejection value was not an error object',
  value: null
})

export const ResponseDataError = TypedError({
  type: 'response-data-error',
  message: 'response data error'
})

export const LandingPageNoContentError = TypedError({
  type: 'landing-page.no-content',
  response: { status: 404 },
  message: 'Content does not exist for landing page'
})

export const LandingPageUnpublishedError = TypedError({
  type: 'landing-page.unpublished',
  response: { status: 404 },
  message: 'Landing page is unpublished'
})

export const MetadataGenerationError = TypedError({
  type: 'metadata',
  title: 'Metadata',
  message: '{title}: {origMessage}'
})

export const MetadataGenerationWarning = TypedError({
  type: 'metadata',
  title: 'Metadata',
  message: '{title}: {origMessage}'
})

export const WebSocketError = TypedError({
  type: 'websocket',
  message: 'websocket error'
})

export const isNetworkError = (error: any) => {
  return get(error, 'message') === 'Network Error'
}

export const isHttpError = (error: any, code: number) => {
  const status = get(error, 'response.status')
  return code === status
}

export const isTimeoutError = (error: any) => {
  const status = get(error, 'response.status')
  const code = get(error, 'code')

  return [408, 504].includes(status) || code === 'ECONNABORTED'
}

export const isCriticalError = (error: any) => {
  const status = get(error, 'response.status')
  return [422, 500, 502, 503].includes(status)
}

export const isWarning = (error: any) => {
  return isHttpError(error, 400)
}

export const isAxiosError = (error: any): error is AxiosError => {
  const request = get(error, 'request')
  const config = get(error, 'config')
  return !!(config && request)
}

export const isIgnoredError = (error: any) => {
  return (
    // Do not log network errors. They are not helpful.
    // We do not look at them anyway and they happens if user has some issues with
    // internet connection, which we can't really control.
    // Reference: https://github.com/envato/market-shopfront/issues/2332
    isNetworkError(error) ||
    // Do not log timeout errors. They took almost 50% of overall logs to shopfront.
    // We do not look at timeout logs and so far we haven't found them useful.
    isTimeoutError(error) ||
    // Do not log HTTP 401 unauthorized errors. Related to expired / invalid
    // authentication tokens. These errors are expected to happen.
    isHttpError(error, 401)
  )
}

export const getAxiosErrorSummary = (
  error: AxiosError,
  maxResponseLength: number
) => {
  const url = error.config.url
  const endpoint = url && url.split('?')[0]
  let message = error.message
  let status
  let responseData

  if (error.response) {
    status = error.response.status
    responseData = error.response.data
    message = `Request [${endpoint}] failed with status code ${status}`

    if (responseData) {
      let nextResponseData = responseData
      if (!isString(nextResponseData)) {
        nextResponseData = JSON.stringify(nextResponseData)
      }
      if (nextResponseData.length > maxResponseLength) {
        responseData = nextResponseData.substr(0, maxResponseLength)
      }
    }
  }

  return {
    url,
    status,
    responseData,
    endpoint,
    message
  }
}

export enum LogErrorLevel {
  critical = 'critical',
  error = 'error',
  warning = 'warning'
}

interface ILogErrorOptions {
  data?: { [key: string]: any }
  request?: Request
  level?: LogErrorLevel
}

/**
 * Log error to rollbar
 * @param {Error} error
 */
export const logError = (error: any, options: ILogErrorOptions = {}): Error => {
  if (isError(error)) {
    const { data = {}, request, level } = options
    let message = error.message

    if (isTimeoutError(error)) {
      collect({
        event: 'networkissue',
        params: {
          type: 'timeout',
          url: get(error, 'config.url'),
          method: get(error, 'config.method'),
          status: get(error, 'response.status'),
          data: get(error, 'response.data')
        }
      })
    }

    if (isIgnoredError(error)) {
      return error
    }

    // Enhance logging for AxiosErrors
    // Originally Rollbar for AxiosErrors logs only message "Request failed with code 404" without
    // any info about endpoint/url or parameters which is not helpful
    if (isAxiosError(error)) {
      data.axios = getAxiosErrorSummary(error, AXIOS_RESPONSE_DATA_MAX_LENGTH)
      message = data.axios.message
    }

    if (level) {
      rollbar[level](message, error, data, request)
    } else {
      if (isWarning(error)) {
        rollbar.warning(message, error, data, request)
      } else {
        rollbar.error(message, error, data, request)
      }
    }

    return error
  } else if (error) {
    return logError(NotAnError({ value: error }))
  } else {
    return logError(UndefinedError())
  }
}
